import { useCallback, useEffect, useRef, useState } from 'react';

// Auto-stop after this much silence (no new speech results) once the user has
// started talking. Web Speech already stops on longer silences, but this makes
// the behaviour predictable and snappy across browsers.
const DEFAULT_SILENCE_TIMEOUT_MS = 2500;

/** Map the app's Google-Translate language code to a BCP-47 speech locale. */
export function getSpeechLang(gtLang) {
  const map = {
    kn: 'kn-IN', // Kannada
    hi: 'hi-IN', // Hindi
    en: 'en-IN', // English (Indian accent models handle local names better)
  };
  return map[(gtLang || '').toLowerCase()] || 'en-IN';
}

export function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function isSpeechRecognitionSupported() {
  return Boolean(getSpeechRecognitionCtor());
}

/**
 * Thin, dependency-free wrapper around the native Web Speech API
 * (`SpeechRecognition` / `webkitSpeechRecognition`).
 *
 * Returns `{ supported, listening, interimTranscript, error, start, stop, clearError }`.
 * - `onInterimResult(text)` fires continuously with the best-so-far transcript.
 * - `onFinalResult(text)` fires once when recognition ends with a usable result.
 */
export default function useSpeechRecognition({
  lang = 'en-IN',
  onInterimResult,
  onFinalResult,
  silenceTimeoutMs = DEFAULT_SILENCE_TIMEOUT_MS,
} = {}) {
  const [listening, setListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const finalTranscriptRef = useRef('');
  // Best-so-far transcript (final + interim). Used as a fallback because some
  // browsers end recognition on short words without ever marking a result
  // `isFinal`, which would otherwise drop the spoken text silently.
  const lastTranscriptRef = useRef('');

  const supported = isSpeechRecognitionSupported();

  // Keep the latest callbacks/lang without forcing `start` to be recreated.
  const onInterimRef = useRef(onInterimResult);
  const onFinalRef = useRef(onFinalResult);
  const langRef = useRef(lang);
  const silenceRef = useRef(silenceTimeoutMs);
  useEffect(() => { onInterimRef.current = onInterimResult; }, [onInterimResult]);
  useEffect(() => { onFinalRef.current = onFinalResult; }, [onFinalResult]);
  useEffect(() => { langRef.current = lang; }, [lang]);
  useEffect(() => { silenceRef.current = silenceTimeoutMs; }, [silenceTimeoutMs]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearSilenceTimer();
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.stop(); } catch (_) { /* already stopped */ }
    }
  }, [clearSilenceTimer]);

  const clearError = useCallback(() => setError(''), []);

  const start = useCallback(() => {
    setError('');

    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('unsupported');
      return;
    }

    // Mic capture requires a secure context. This is the usual reason voice
    // search "does nothing" when the app is opened over a LAN IP on a phone
    // (e.g. http://192.168.x.x:3000) instead of https:// or localhost.
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setError('insecure-context');
      return;
    }

    // Never keep two sessions alive at once.
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) { /* noop */ }
      recognitionRef.current = null;
    }

    const rec = new Ctor();
    rec.lang = langRef.current;
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    finalTranscriptRef.current = '';
    lastTranscriptRef.current = '';
    setInterimTranscript('');

    rec.onstart = () => setListening(true);

    rec.onresult = (event) => {
      clearSilenceTimer();

      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const chunk = event.results[i][0]?.transcript || '';
        if (event.results[i].isFinal) final += chunk;
        else interim += chunk;
      }

      if (final) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${final}`.trim();
      }

      const combined = `${finalTranscriptRef.current} ${interim}`.trim();
      if (combined) lastTranscriptRef.current = combined;
      setInterimTranscript(combined);
      if (combined && onInterimRef.current) onInterimRef.current(combined);

      // Restart the silence countdown on every new bit of speech.
      silenceTimerRef.current = setTimeout(() => {
        try { rec.stop(); } catch (_) { /* noop */ }
      }, silenceRef.current);
    };

    rec.onerror = (event) => {
      // `aborted` happens when we programmatically stop/replace — not a real error.
      if (event?.error && event.error !== 'aborted') {
        setError(event.error);
      }
    };

    rec.onend = () => {
      clearSilenceTimer();
      setListening(false);
      // Prefer the confirmed final transcript, but fall back to the best
      // interim text so short single words still trigger a search.
      const result = (finalTranscriptRef.current || lastTranscriptRef.current).trim();
      recognitionRef.current = null;
      if (result && onFinalRef.current) onFinalRef.current(result);
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch (_) {
      // start() throws if called while already starting; reset defensively.
      recognitionRef.current = null;
      setListening(false);
      setError('start-failed');
    }
  }, [clearSilenceTimer]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      const rec = recognitionRef.current;
      if (rec) {
        try { rec.abort(); } catch (_) { /* noop */ }
      }
      recognitionRef.current = null;
    };
  }, [clearSilenceTimer]);

  return { supported, listening, interimTranscript, error, start, stop, clearError };
}
